import React, { useEffect, useState, useCallback } from 'react';
import { analyticsAPI, exportAPI, downloadBlob } from '../api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Download, Filter, X, MapPin, ChevronRight } from 'lucide-react';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);
const RANGES = [['month', 'This Month'], ['quarter', 'This Quarter'], ['year', 'This Year'], ['custom', 'Custom']];
const COLORS = ['#F59E0B', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
const EMPTY_FILTERS = { source: '', client_sector: '', city: '', service: '', budget_band: '', executive: '', status: '',
  country: '', country_name: '', state: '', district: '' };
const LEVEL_TITLE = { country: 'Country', state: 'State / Region', district: 'District', city: 'City' };

export const LeadPerformancePage = () => {
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('year');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });

  const buildParams = useCallback(() => {
    const p = {};
    if (range === 'custom') { if (customFrom) p.date_from = customFrom; if (customTo) p.date_to = customTo; }
    else p.period = range;
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== 'country_name') p[k] = v; });
    return p;
  }, [range, customFrom, customTo, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await analyticsAPI.leadPerformance(buildParams()); setData(res.data); }
    catch (e) { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);

  const doExport = async (fmt) => {
    try { const res = await exportAPI.download('lead-performance', { ...buildParams(), fmt }); downloadBlob(res, `lead-performance.${fmt}`); toast.success('Export ready'); }
    catch (e) { toast.error('Export failed'); }
  };

  const opts = data?.filter_options || {};
  const kpis = data?.kpis || {};

  const Table = ({ title, rows, dimKey, showName, onRowClick, dimLabel, subtitle }) => (
    <div className="clay-card p-4" data-testid={`lp-table-${dimKey}`}>
      <h3 className="font-medium">{title}</h3>
      {subtitle && <div className="mb-2 text-xs text-muted-foreground">{subtitle}</div>}
      {!subtitle && <div className="mb-3" />}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground"><tr>
            <th className="py-1 pr-2">{dimLabel || title.replace('Performance', '').trim()}</th>
            <th>Leads</th><th>Converted</th><th>Conv%</th><th>Avg Budget</th><th>Business</th>
          </tr></thead>
          <tbody>
            {(rows || []).map((r) => (
              <tr key={r.key} className="border-t border-slate-100 dark:border-slate-800 hover:bg-primary/5 cursor-pointer"
                  data-testid={`lp-row-${dimKey}`}
                  onClick={() => {
                    if (onRowClick) { onRowClick(r); return; }
                    if (dimKey !== 'budget') setFilters(f => ({ ...f, [dimKey]: r.key === 'Unspecified' ? '' : r.key }));
                    else setFilters(f => ({ ...f, budget_band: r.key }));
                  }}>
                <td className="py-1 pr-2 font-medium">{showName ? (r.name || r.key) : r.key}</td>
                <td>{r.leads}</td><td>{r.converted}</td><td>{r.conversion_rate}%</td><td>{money(r.avg_budget)}</td><td>{money(r.business)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const loc = data?.location || {};
  const crumbs = [
    { label: 'Worldwide', onClick: () => setFilters(f => ({ ...f, country: '', country_name: '', state: '', district: '', city: '' })) },
    filters.country && { label: filters.country_name || loc.selected?.country_name || filters.country,
      onClick: () => setFilters(f => ({ ...f, state: '', district: '', city: '' })) },
    filters.state && { label: filters.state, onClick: () => setFilters(f => ({ ...f, district: '', city: '' })) },
    filters.district && { label: filters.district, onClick: () => setFilters(f => ({ ...f, city: '' })) },
    filters.city && { label: filters.city, onClick: () => {} },
  ].filter(Boolean);

  const drillLocation = (r) => {
    if (loc.level === 'country') {
      // Unspecified = legacy free-text locations; they stay analysable instead of vanishing
      if (r.key === 'Unspecified') { setFilters(f => ({ ...f, country: 'none', country_name: 'Unspecified (legacy)', state: '', district: '', city: '' })); return; }
      setFilters(f => ({ ...f, country: r.code || '', country_name: r.key, state: '', district: '', city: '' }));
      return;
    }
    if (r.key === 'Unspecified') return;
    if (loc.level === 'state') setFilters(f => ({ ...f, state: r.key, district: '', city: '' }));
    else if (loc.level === 'district') setFilters(f => ({ ...f, district: r.key, city: '' }));
    else setFilters(f => ({ ...f, city: r.key }));
  };

  const activeFilters = Object.entries(filters).filter(([k, v]) => v && k !== 'country_name');

  return (
    <div className="space-y-6" data-testid="lead-performance-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-semibold flex items-center gap-2"><TrendingUp className="h-6 w-6 text-primary" /> Marketing Intelligence</h1>
          <p className="text-sm text-muted-foreground">Lead & source performance across platforms, locations, budgets and services.</p>
        </div>
        {hasPermission('report.export') && (
          <div className="flex gap-2">
            <button onClick={() => doExport('xlsx')} data-testid="lp-export-xlsx" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20"><Download className="h-4 w-4" /> Excel</button>
            <button onClick={() => doExport('csv')} data-testid="lp-export-csv" className="inline-flex items-center gap-1 px-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 rounded-lg">CSV</button>
          </div>
        )}
      </div>

      {/* Range + filters */}
      <div className="clay-card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {RANGES.map(([k, label]) => (
            <button key={k} onClick={() => setRange(k)} data-testid={`lp-range-${k}`}
              className={`px-3 py-1.5 rounded-full text-sm ${range === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{label}</button>
          ))}
          {range === 'custom' && (
            <span className="flex items-center gap-2">
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-testid="lp-custom-from" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} data-testid="lp-custom-to" className="px-2 py-1 border rounded-lg text-sm bg-white dark:bg-slate-900" />
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))} data-testid="lp-filter-source" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Sources</option>{(opts.sources || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.client_sector} onChange={e => setFilters(f => ({ ...f, client_sector: e.target.value }))} data-testid="lp-filter-sector" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Sectors</option>{(opts.sectors || []).map(s => <option key={s.sector_id} value={s.sector_id}>{s.name}</option>)}
          </select>
          <select value={filters.country} onChange={e => setFilters(f => ({ ...f, country: e.target.value,
            country_name: (opts.countries || []).find(c => c.code === e.target.value)?.name || '', state: '', district: '', city: '' }))}
            data-testid="lp-filter-country" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Countries</option>
            {(opts.countries || []).map(c => <option key={c.code || c.name} value={c.code || ''}>{c.name}</option>)}
            <option value="none">Unspecified (legacy)</option>
          </select>
          {filters.country && filters.country !== 'none' && (
            <select value={filters.state} onChange={e => setFilters(f => ({ ...f, state: e.target.value, district: '', city: '' }))}
              data-testid="lp-filter-state" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
              <option value="">All {loc.labels?.state || 'States'}</option>{(opts.states || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          <select value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} data-testid="lp-filter-city" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Cities</option>{(opts.cities || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.service} onChange={e => setFilters(f => ({ ...f, service: e.target.value }))} data-testid="lp-filter-service" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Services</option>{(opts.services || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.budget_band} onChange={e => setFilters(f => ({ ...f, budget_band: e.target.value }))} data-testid="lp-filter-budget" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Budgets</option>{(opts.budget_bands || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} data-testid="lp-filter-status" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
            <option value="">All Statuses</option>{(opts.statuses || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(opts.executives || []).length > 0 && (
            <select value={filters.executive} onChange={e => setFilters(f => ({ ...f, executive: e.target.value }))} data-testid="lp-filter-executive" className="px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
              <option value="">All Executives</option>{opts.executives.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          {activeFilters.length > 0 && (
            <button onClick={() => setFilters({ ...EMPTY_FILTERS })} data-testid="lp-clear-filters" className="inline-flex items-center gap-1 px-2 py-1 text-primary"><X className="h-3 w-3" /> Clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="lp-kpis">
            {[['Total Leads', kpis.total_leads], ['Unique', kpis.unique_leads], ['Qualified', kpis.qualified_leads], ['Hot', kpis.hot_leads], ['Converted', kpis.converted], ['Lost', kpis.lost],
              ['Conversion', `${kpis.conversion_rate}%`], ['Avg Budget', money(kpis.avg_lead_budget)], ['Total Budget', money(kpis.total_lead_budget)], ['Business', money(kpis.business_generated)], ['Avg Deal', money(kpis.avg_deal_value)], ['Avg Close', kpis.avg_closing_time != null ? `${kpis.avg_closing_time}d` : '\u2014']].map(([label, val]) => (
              <div key={label} className="clay-card p-3"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-semibold">{val ?? 0}</div></div>
            ))}
          </div>

          {/* Insights */}
          {data?.insights && (
            <div className="clay-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="lp-insights">
              <div><div className="text-xs text-muted-foreground">Most Leads From</div><div className="font-semibold text-primary">{data.insights.best_lead_source || '\u2014'}</div></div>
              <div><div className="text-xs text-muted-foreground">Best Converting Source</div><div className="font-semibold text-green-600">{data.insights.best_conversion_source || '\u2014'}</div></div>
              <div><div className="text-xs text-muted-foreground">Highest Revenue Source</div><div className="font-semibold text-amber-600">{data.insights.highest_revenue_source || '\u2014'}</div></div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="clay-card p-4">
              <h3 className="font-medium mb-3">Leads & Conversion by Source</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data?.by_source || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="key" fontSize={10} /><YAxis fontSize={10} /><Tooltip />
                  <Bar dataKey="leads" fill="#3B82F6" radius={[6, 6, 0, 0]} name="Leads" />
                  <Bar dataKey="converted" fill="#10B981" radius={[6, 6, 0, 0]} name="Converted" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="clay-card p-4">
              <h3 className="font-medium">Business by Location</h3>
              <div className="mb-2 text-xs text-muted-foreground" data-testid="lp-pie-level">
                by {LEVEL_TITLE[loc.level] || 'Location'}{filters.country ? ` \u00b7 ${filters.country_name || filters.country}` : ''}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={(loc.rows || []).filter(r => r.business > 0)} dataKey="business" nameKey="key" cx="50%" cy="50%" outerRadius={90} label={(e) => e.key}>
                    {(loc.rows || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Table title="Source Performance" rows={data?.by_source} dimKey="source" />
            <div className="space-y-2" data-testid="lp-location-panel">
              <div className="flex flex-wrap items-center gap-1 text-xs" data-testid="lp-location-breadcrumb">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                {crumbs.map((c, i) => (
                  <React.Fragment key={c.label}>
                    {i > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                    <button onClick={c.onClick} data-testid={`lp-crumb-${i}`}
                      className={i === crumbs.length - 1 ? 'font-semibold text-primary' : 'text-muted-foreground hover:text-primary'}>{c.label}</button>
                  </React.Fragment>
                ))}
              </div>
              <Table title="Location Performance" rows={loc.rows} dimKey="location"
                dimLabel={LEVEL_TITLE[loc.level] || 'Location'} onRowClick={drillLocation}
                subtitle={`Business by ${(LEVEL_TITLE[loc.level] || 'location').toLowerCase()}${loc.level === 'city' ? '' : ' \u00b7 click a row to drill down'}${filters.source ? ` \u00b7 source: ${filters.source}` : ''}`} />
            </div>
            <div className="clay-card p-4" data-testid="lp-table-sector">
              <h3 className="font-medium">Leads by Client Sector</h3>
              <div className="mb-2 text-xs text-muted-foreground">Click a row to filter everything by that sector</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground"><tr>
                    <th className="py-1 pr-2">Sector</th><th>Total Leads</th><th>Qualified</th><th>Converted</th><th>Conv%</th>
                  </tr></thead>
                  <tbody>
                    {(data?.by_sector || []).map((r) => (
                      <tr key={r.key} data-testid="lp-row-sector"
                        onClick={() => setFilters(f => ({ ...f, client_sector: r.sector_id || '' }))}
                        className="cursor-pointer border-t border-slate-100 hover:bg-primary/5 dark:border-slate-800">
                        <td className="py-1 pr-2 font-medium">{r.key}</td>
                        <td>{r.leads}</td><td>{r.qualified}</td><td>{r.converted}</td><td>{r.conversion_rate}%</td>
                      </tr>
                    ))}
                    {(data?.by_sector || []).length === 0 && (
                      <tr><td colSpan={5} className="py-2 text-muted-foreground">No sector data for this selection</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <Table title="Budget Performance" rows={data?.by_budget} dimKey="budget" />
            <Table title="Service Performance" rows={data?.by_service} dimKey="service" />
            {(data?.by_executive || []).length > 0 && <Table title="Executive Performance" rows={data?.by_executive} dimKey="executive" showName />}
          </div>
        </>
      )}
    </div>
  );
};
export default LeadPerformancePage;
